import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { useToast } from '../../hooks/use-toast';
import {
  Award,
  Download,
  Share2,
  CheckCircle2,
  Calendar,
  Star,
  BookOpen,
  Home,
  Trophy
} from 'lucide-react';

const ARCCertificate = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [certData, setCertData] = useState(null);
  const [notCertified, setNotCertified] = useState(false);

  const fetchCertificate = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/arc/certificate');
      setCertData(response.data);
    } catch (error) {
      console.error('Failed to fetch certificate:', error);
      if (error.response?.status === 400) {
        setNotCertified(true);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load certificate',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  }, [api, toast]);

  useEffect(() => {
    fetchCertificate();
  }, [fetchCertificate]);

  const downloadCertificate = async () => {
    try {
      setDownloading(true);
      const response = await api.get('/arc/certificate/download', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ARC_Certificate_${certData.certificate_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: 'Downloaded!',
        description: 'Your certificate has been downloaded.',
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to download certificate. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setDownloading(false);
    }
  };

  const shareCertificate = async () => {
    const shareText = `I just earned my Advanced Recruitment Certification (ARC)! Certificate ID: ${certData.certificate_id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'ARC Certification',
          text: shareText,
          url: window.location.href
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share error:', err);
        }
      }
    } else {
      // Fallback to copy to clipboard
      navigator.clipboard.writeText(shareText);
      toast({
        title: 'Copied!',
        description: 'Share text copied to clipboard.',
      });
    }
  };

  // Format date
  const formatDate = (isoDate) => {
    try {
      return new Date(isoDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return isoDate;
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-[400px]" />
        </div>
      </MainLayout>
    );
  }

  if (notCertified) {
    return (
      <MainLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Card className="max-w-md text-center p-8">
            <BookOpen className="h-16 w-16 mx-auto mb-4 text-amber-500" />
            <h2 className="text-2xl font-bold mb-2">Not Yet Certified</h2>
            <p className="text-muted-foreground mb-6">
              Complete all chapters and pass the final exam to earn your certificate.
            </p>
            <Button onClick={() => navigate('/arc')}>
              Continue Learning
            </Button>
          </Card>
        </div>
      </MainLayout>
    );
  }

  if (!certData) {
    return (
      <MainLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Card className="p-8 text-center">
            <p>Unable to load certificate. Please try again.</p>
            <Button onClick={fetchCertificate} className="mt-4">Retry</Button>
          </Card>
        </div>
      </MainLayout>
    );
  }

  // Determine honor level
  let honorLevel = '';
  let honorColor = '';
  if (certData.final_score >= 90) {
    honorLevel = 'With Distinction';
    honorColor = 'from-amber-400 to-yellow-500';
  } else if (certData.final_score >= 80) {
    honorLevel = 'With Honors';
    honorColor = 'from-emerald-400 to-teal-500';
  }

  return (
    <MainLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/arc')} data-testid="back-to-dashboard-btn">
            <Home className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={shareCertificate} data-testid="share-certificate-btn">
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
            <Button 
              onClick={downloadCertificate}
              disabled={downloading}
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
              data-testid="download-pdf-btn"
            >
              {downloading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Certificate Preview */}
        <Card className="overflow-hidden">
          <div 
            className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-amber-950/50 dark:via-yellow-950/50 dark:to-orange-950/50 p-8"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f59e0b' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          >
            <div className="border-4 border-amber-400 rounded-lg p-8 bg-white/90 dark:bg-slate-900/90">
              {/* Award Icon */}
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <div className="p-4 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg">
                    <Trophy className="h-12 w-12 text-white" />
                  </div>
                  <CheckCircle2 className="h-6 w-6 text-emerald-500 absolute -bottom-1 -right-1 bg-white rounded-full" />
                </div>
              </div>

              {/* Header */}
              <p className="text-center text-sm text-muted-foreground tracking-widest mb-2">
                CERTIFICATE OF ACHIEVEMENT
              </p>
              
              <h1 className="text-3xl md:text-4xl font-bold text-center bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-4">
                Advanced Recruitment<br />Certification
              </h1>

              {/* Certifies */}
              <p className="text-center text-muted-foreground mb-2">This is to certify that</p>
              
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
                {certData.user_name}
              </h2>

              <p className="text-center text-muted-foreground mb-6 max-w-md mx-auto">
                has successfully completed the Advanced Recruitment Certification program
                and demonstrated proficiency in technical recruiting excellence.
              </p>

              {/* Score */}
              <div className="flex justify-center mb-6">
                <div className={`px-6 py-3 rounded-full bg-gradient-to-r ${honorLevel ? honorColor : 'from-emerald-500 to-teal-500'} text-white font-bold`}>
                  <Star className="h-4 w-4 inline mr-2" />
                  Final Score: {certData.final_score.toFixed(1)}%
                  {honorLevel && ` - ${honorLevel}`}
                </div>
              </div>

              {/* Course Details */}
              <p className="text-center text-sm text-muted-foreground mb-6">
                Completed {certData.total_modules} modules and {certData.total_chapters} chapters covering:<br />
                Recruiting Fundamentals, Technical Knowledge, Client Management,<br />
                Candidate Sourcing, Screening Excellence, Negotiation, and Professional Development
              </p>

              {/* Date and ID */}
              <div className="flex justify-center gap-12 mt-8">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Date of Completion</p>
                  <p className="font-semibold flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-amber-600" />
                    {formatDate(certData.completion_date)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Certificate ID</p>
                  <p className="font-semibold font-mono">{certData.certificate_id}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Additional Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4 flex items-center gap-3">
              <Award className="h-8 w-8 text-amber-500" />
              <div>
                <p className="font-semibold">Certification Valid</p>
                <p className="text-sm text-muted-foreground">Lifetime credential</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-blue-500" />
              <div>
                <p className="font-semibold">{certData.total_chapters} Chapters</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              <div>
                <p className="font-semibold">All Quizzes Passed</p>
                <p className="text-sm text-muted-foreground">With verbal assessment</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Verification Note */}
        <Card className="bg-muted/50">
          <CardContent className="pt-4 text-center">
            <p className="text-sm text-muted-foreground">
              This certificate can be verified using Certificate ID: <span className="font-mono font-semibold">{certData.certificate_id}</span>
            </p>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ARCCertificate;
